import { apiClient as client } from './client';

export interface CreatorProfile {
  id: number;
  character_id: number;
  tier: 'basic' | 'premium' | 'exclusive';
  monthly_price: number;
  currency: string;
  allow_ppv: boolean;
  ppv_price_range_min: number;
  ppv_price_range_max: number;
  auto_post_enabled: boolean;
  post_frequency_min_hours: number;
  post_frequency_max_hours: number;
  auto_comment_enabled: boolean;
  comment_delay_min_minutes: number;
  comment_delay_max_minutes: number;
  content_warnings: string[];
  age_restriction: number;
  total_subscribers: number;
  simulated_subscribers?: number;
  display_subscribers?: number; // bevorzugt anzeigen, falls vorhanden
  total_posts: number;
  total_earnings: number;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  updated_at?: string;
  character?: {
    id: number;
    name: string;
    description?: string;
    avatar_url?: string;
    // Optional erweitertes Feld für UI (Blur/Badges) – vom Backend geliefert
    gallery?: Array<{
      id: number;
      url: string;
      is_profile_image?: boolean;
      is_fsk18?: boolean;
      should_blur?: boolean;
    }>;
  };
}

export interface CreatorSubscription {
  id: number;
  user_id: number;
  creator_id: number;
  monthly_price: number;
  currency: string;
  status: 'active' | 'expired' | 'cancelled' | 'pending';
  started_at: string;
  expires_at: string;
  next_billing_date: string;
  cancelled_at?: string;
  low_balance_notified: boolean;
  renewal_reminder_sent: boolean;
  created_at: string;
  updated_at?: string;
  creator?: {
    id: number;
    character_id: number;
    character?: {
      id: number;
      name: string;
      avatar_url?: string;
    };
  };
  // Timeline-style avatar data added by backend
  creator_avatar?: string;
  creator_name?: string;
}

export interface CreatorPost {
  id: number;
  creator_id: number;
  title?: string;
  content: string;
  post_type: 'text' | 'image' | 'video' | 'audio' | 'mixed';
  media_urls: string[];
  thumbnail_url?: string;
  is_paid: boolean;
  price?: number;
  currency: string;
  likes_count: number;
  simulated_likes_count?: number;
  display_likes_count?: number; // bevorzugt anzeigen, falls vorhanden
  comments_count: number;
  simulated_comments_count?: number;
  display_comments_count?: number; // bevorzugt anzeigen, falls vorhanden
  views_count: number;
  simulated_views_count?: number;
  display_views_count?: number; // bevorzugt anzeigen, falls vorhanden
  // Admin-only Zusatzfeld
  unlocks_count?: number;
  status: 'draft' | 'published' | 'scheduled' | 'archived';
  published_at?: string;
  scheduled_at?: string;
  generated_by_ai: boolean;
  generation_prompt?: string;
  created_at: string;
  updated_at?: string;
  user_has_liked?: boolean;
  user_has_unlocked?: boolean;
}

export interface PostComment {
  id: number;
  post_id: number;
  user_id?: number; // Nullable für simulierte Kommentare
  user?: {
    id: number;
    username: string;
    email?: string;
  };
  content: string;
  ai_response?: string;
  ai_responded_at?: string;
  created_at: string;
  updated_at?: string;
  // Threading support
  parent_comment_id?: number;
  reply_to_ai?: boolean;
  replies?: PostComment[];
  // Additional fields for UI
  user_name?: string;
  creator_name?: string;
  // Simulation fields - für KI-generierte Fake-Kommentare
  is_simulated?: boolean;
  simulated_username?: string;
  simulated_display_name?: string;
  simulated_avatar_seed?: string;
}

export interface CreatorStats {
  total_posts: number;
  total_subscribers: number;
  total_earnings: number;
  average_likes_per_post: number;
  average_comments_per_post: number;
  paid_posts_count: number;
  paid_posts_revenue: number;
  subscription_revenue: number;
  currency: string;
}

export interface TimelinePost extends CreatorPost {
  creator_name: string;
  creator_avatar?: string;
  is_subscribed: boolean;
  creator_username?: string;
}

export interface CreatorMedia {
  id: number;
  filename: string;
  original_filename: string;
  media_type: 'image' | 'video' | 'audio' | 'gif';
  category: 'profile' | 'posts' | 'preview' | 'exclusive' | 'story';
  title?: string;
  description?: string;
  is_public: boolean;
  is_featured: boolean;
  times_used: number;
  created_at: string;
  url: string;
}

// Creator management
export const creatorSpaceApi = {
  // Creator profiles
  getCreators: async (skip = 0, limit = 50): Promise<CreatorProfile[]> => {
    const response = await client.get(`/creator-space/creators?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  getCreator: async (creatorId: number): Promise<CreatorProfile> => {
    const response = await client.get(`/creator-space/creators/${creatorId}`);
    return response.data;
  },

  // Get creator ID by character ID - for quick navigation
  getCreatorByCharacterId: async (characterId: number): Promise<{ creator_id: number; character_id: number } | null> => {
    try {
      const response = await client.get(`/creator-space/by-character/${characterId}`);
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  getCreatorStats: async (creatorId: number): Promise<CreatorStats> => {
    const response = await client.get(`/creator-space/creators/${creatorId}/stats`);
    return response.data;
  },

  // Subscriptions
  subscribeToCreator: async (creatorId: number): Promise<CreatorSubscription> => {
    const response = await client.post(`/creator-space/creators/${creatorId}/subscribe`);
    return response.data;
  },

  unsubscribeFromCreator: async (creatorId: number): Promise<void> => {
    await client.delete(`/creator-space/creators/${creatorId}/subscribe`);
  },

  getMySubscriptions: async (): Promise<CreatorSubscription[]> => {
    const response = await client.get('/creator-space/subscriptions');
    return response.data;
  },

  isSubscribed: async (creatorId: number): Promise<boolean> => {
    const response = await client.get(`/creator-space/creators/${creatorId}/is-subscribed`);
    return response.data.is_subscribed;
  },

  // Posts and timeline
  getTimeline: async (skip = 0, limit = 20): Promise<TimelinePost[]> => {
    const response = await client.get(`/creator-space/timeline?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  getCreatorPosts: async (creatorId: number, skip = 0, limit = 20): Promise<CreatorPost[]> => {
    const response = await client.get(`/creator-space/creators/${creatorId}/posts?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  getPost: async (postId: number): Promise<CreatorPost> => {
    const response = await client.get(`/creator-space/posts/${postId}`);
    return response.data;
  },

  // Post interactions
  likePost: async (postId: number): Promise<void> => {
    await client.post(`/creator-space/posts/${postId}/like`);
  },

  unlikePost: async (postId: number): Promise<void> => {
    await client.delete(`/creator-space/posts/${postId}/like`);
  },

  unlockPost: async (postId: number): Promise<void> => {
    await client.post(`/creator-space/posts/${postId}/unlock`);
  },

  // Views
  viewPost: async (postId: number): Promise<void> => {
    await client.post(`/creator-space/posts/${postId}/view`);
  },

  // Comments
  createComment: async (postId: number, content: string, parentCommentId?: number): Promise<PostComment> => {
    const response = await client.post(`/creator-space/posts/${postId}/comments`, {
      content,
      parent_comment_id: parentCommentId
    });
    return response.data;
  },

  getPostComments: async (postId: number, skip = 0, limit = 50): Promise<PostComment[]> => {
    const response = await client.get(`/creator-space/posts/${postId}/comments?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  // Chat access
  canChatWithCreator: async (characterId: number): Promise<boolean> => {
    const response = await client.get(`/creator-space/characters/${characterId}/can-chat`);
    return response.data.can_chat;
  },

  // Creator media
  getCreatorMedia: async (
    creatorId: number,
    category?: string,
    mediaType?: string,
    skip = 0,
    limit = 50
  ): Promise<CreatorMedia[]> => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString()
    });
    
    if (category) params.append('category', category);
    if (mediaType) params.append('media_type', mediaType);
    
    const response = await client.get(`/creator-gallery/creators/${creatorId}/media?${params}`);
    return response.data;
  },

  uploadCreatorMedia: async (
    creatorId: number,
    file: File,
    options?: {
      title?: string;
      description?: string;
      category?: string;
      isPublic?: boolean;
    }
  ): Promise<CreatorMedia> => {
    const formData = new FormData();
    formData.append('file', file);
    
    if (options?.title) formData.append('title', options.title);
    if (options?.description) formData.append('description', options.description);
    if (options?.category) formData.append('category', options.category);
    if (options?.isPublic !== undefined) formData.append('is_public', options.isPublic.toString());
    
    const response = await client.post(`/creator-gallery/creators/${creatorId}/media/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Admin functions
  admin: {
    createCreator: async (creatorData: {
      character_id: number;
      tier?: string;
      monthly_price: number;
      currency?: string;
      allow_ppv?: boolean;
      ppv_price_range_min?: number;
      ppv_price_range_max?: number;
      auto_post_enabled?: boolean;
      post_frequency_min_hours?: number;
      post_frequency_max_hours?: number;
      auto_comment_enabled?: boolean;
      comment_delay_min_minutes?: number;
      comment_delay_max_minutes?: number;
      content_warnings?: string[];
      age_restriction?: number;
    }): Promise<CreatorProfile> => {
      const response = await client.post('/creator-space/creators', creatorData);
      return response.data;
    },

    updateCreator: async (creatorId: number, updateData: Partial<CreatorProfile>): Promise<CreatorProfile> => {
      const response = await client.put(`/creator-space/creators/${creatorId}`, updateData);
      return response.data;
    },

    getAllCreators: async (skip = 0, limit = 100): Promise<CreatorProfile[]> => {
      const response = await client.get(`/creator-space/admin/creators?skip=${skip}&limit=${limit}`);
      return response.data;
    },

    getExpiringSubscriptions: async (daysAhead = 3): Promise<CreatorSubscription[]> => {
      const response = await client.get(`/creator-space/admin/expiring-subscriptions?days_ahead=${daysAhead}`);
      return response.data;
    },

    getNextScheduledPost: async (
      creatorId: number
    ): Promise<{ creator_id: number; scheduled_time: string | null; status: string | null; schedule_id?: number }> => {
      const response = await client.get(`/creator-space/admin/creators/${creatorId}/next-scheduled-post`);
      return response.data;
    },

    ensureNextScheduledPost: async (
      creatorId: number
    ): Promise<{ creator_id: number; scheduled_time: string | null; status: string | null; schedule_id?: number; task_id?: string }> => {
      const response = await client.post(`/creator-space/admin/creators/${creatorId}/ensure-next-scheduled-post`);
      return response.data;
    },

    createPost: async (creatorId: number, postData: {
      title?: string;
      content: string;
      post_type?: string;
      media_urls?: string[];
      thumbnail_url?: string;
      is_paid?: boolean;
      price?: number;
      currency?: string;
      scheduled_at?: string;
    }): Promise<CreatorPost> => {
      const response = await client.post(`/creator-space/creators/${creatorId}/posts`, postData);
      return response.data;
    }
    ,
    triggerCreatorPost: async (
      creatorId: number
    ): Promise<{ message: string; task_id: string; creator_id: number }> => {
      const response = await client.post(`/creator-space/admin/trigger-creator-post?creator_id=${creatorId}`);
      return response.data;
    },
    listCreatorPosts: async (
      creatorId: number,
      skip = 0,
      limit = 50
    ): Promise<CreatorPost[]> => {
      const response = await client.get(`/creator-space/admin/creators/${creatorId}/posts?skip=${skip}&limit=${limit}`);
      return response.data;
    },
    deletePost: async (postId: number): Promise<{ message: string }> => {
      const response = await client.delete(`/creator-space/admin/posts/${postId}`);
      return response.data;
    },

    // Simulation admin
    getSimulationSettings: async (): Promise<Record<string, any>> => {
      const response = await client.get('/admin/creator-space/simulation/settings');
      return response.data;
    },
    updateSimulationSettings: async (updates: Record<string, any>): Promise<Record<string, any>> => {
      const response = await client.put('/admin/creator-space/simulation/settings', updates);
      return response.data;
    },
    triggerSimulation: async (): Promise<{ success: boolean; fans_updated_creators: number; likes_updated_posts: number; views_updated_posts?: number; comments_updated_posts?: number; consistency_fixed_posts?: number }> => {
      const response = await client.post('/admin/creator-space/simulation/trigger');
      return response.data;
    },
    
    // Fix metric consistency (Views > Likes > Comments)
    fixConsistency: async (): Promise<{ success: boolean; fixed_posts: number; message: string }> => {
      const response = await client.post('/admin/creator-space/simulation/fix-consistency');
      return response.data;
    },
    
    // Simulated Comments API
    getSimulatedCommentsSettings: async (): Promise<Record<string, any>> => {
      const response = await client.get('/admin/creator-space/simulated-comments/settings');
      return response.data;
    },
    updateSimulatedCommentsSettings: async (updates: Record<string, any>): Promise<Record<string, any>> => {
      const response = await client.put('/admin/creator-space/simulated-comments/settings', updates);
      return response.data;
    },
    getSimulatedCommentsStats: async (): Promise<{ 
      total_simulated_comments: number; 
      with_ai_response: number; 
      pending_ai_response: number; 
      queue_pending: number;
      queue_comment_posted: number;
      queue_completed: number;
      next_scheduled_comment: string | null;
      settings: Record<string, any> 
    }> => {
      const response = await client.get('/admin/creator-space/simulated-comments/stats');
      return response.data;
    },
    triggerSimulatedComments: async (): Promise<{ success: boolean; task_ids: string[]; message: string }> => {
      const response = await client.post('/admin/creator-space/simulated-comments/trigger');
      return response.data;
    },
    triggerBatchGeneration: async (): Promise<{ success: boolean; task_id: string; message: string }> => {
      const response = await client.post('/admin/creator-space/simulated-comments/generate-batch');
      return response.data;
    },
    clearCommentQueue: async (status?: string): Promise<{ success: boolean; deleted: number; message: string }> => {
      const response = await client.delete('/admin/creator-space/simulated-comments/queue', { params: { status } });
      return response.data;
    },
    triggerSinglePostComment: async (postId: number): Promise<{ success: boolean; task_id: string; post_id: number; message: string }> => {
      const response = await client.post(`/admin/creator-space/simulated-comments/trigger-post/${postId}`);
      return response.data;
    }
  }
};

export default creatorSpaceApi;